import {
  APPRAISIFY_ADDRESS,
  APPRAISIFY_NAME,
  AloftArea,
  AloftDate,
  AloftPrice,
  AloftTime,
  CompType,
  PriorSales,
  PropertyIdentification,
  RESOMedia,
  RecommendedCompsStatistics,
  SubjectData,
  addressToString,
  formatLongRevisionType,
  formatShortenedPropertyType,
  noop,
  orUndefined,
} from '@aloft/toolkit-shared';

import { useEffect, useMemo, useState } from 'react';

import { createCircleCoordinates } from '../../../components/RecommendedCompSelection/hooks/createCircleCoordinates.ts';
import { useRecommendedProperties } from '../../../components/RecommendedCompSelection/hooks/useRecommendedProperties.ts';
import { fetchGetBlob } from '../../../hooks/api/toolkitComponents.ts';
import { useToolkitContext } from '../../../hooks/api/toolkitContext.ts';
import { useStreetViewStaticUrl } from '../../../hooks/googleMaps/useStreetViewStaticUrl.ts';
import { selectFullNameWithDefault } from '../../../redux/features/selectors/accountSelectors.ts';
import { selectCompAdjustments } from '../../../redux/features/selectors/adjustmentSelectors.ts';
import { selectAllCostApproachExportData } from '../../../redux/features/selectors/commentarySelectors/selectAllCostApproachExportData.ts';
import { selectCommentaryV2 } from '../../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { selectCurrentMarketAggregateData } from '../../../redux/features/selectors/commentarySelectors/selectCurrentMarketAggregateData.ts';
import { selectMarketAdjustmentsCommentaryData } from '../../../redux/features/selectors/commentarySelectors/selectMarketAdjustmentsCommentary.ts';
import { selectGarageCarportCost } from '../../../redux/features/selectors/costSelectors/selectGarageCarportCost.ts';
import {
  selectDepreciation,
  selectExternalDepreciation,
  selectFunctionalDepreciation,
  selectPhysicalDepreciation,
} from '../../../redux/features/selectors/depreciationSelectors.ts';
import { selectOpinionOfSiteValuePrice } from '../../../redux/features/selectors/opinionOfSiteValueSelectors.ts';
import {
  selectAdjustmentSettings,
  selectCompSequence,
  selectCompTypes,
  selectEffectiveYear,
  selectIsArvOrder,
  selectOrder,
} from '../../../redux/features/selectors/orderSelectors.ts';
import { selectPriorSales } from '../../../redux/features/selectors/priorSalesSelectors.ts';
import {
  selectImprovementsBasement,
  selectImprovementsGarageCarport,
  selectImprovementsGla,
} from '../../../redux/features/selectors/propertyDataSelectors/improvementsSelectors.ts';
import { selectManualAdjustmentsEntities } from '../../../redux/features/selectors/propertyDataSelectors/manualAdjustmentsSelectors.ts';
import { selectPropertyDataEntities } from '../../../redux/features/selectors/propertyDataSelectors/propertyDataSelectors.ts';
import {
  selectAllAdjustmentsTableData,
  selectAllArvAdjustmentsTableData,
} from '../../../redux/features/selectors/selectAllAdjustmentsTableData.ts';
import {
  selectAdditionalStructureCostNew,
  selectCostNew,
} from '../../../redux/features/selectors/selectCostNew.ts';
import { selectDepreciatedCostOfImprovements } from '../../../redux/features/selectors/selectDepreciatedCostOfImprovements.ts';
import { selectGlaAndBathCostNew } from '../../../redux/features/selectors/selectGlaAndBathCostNew.ts';
import {
  selectOrderHasExternalDepreciation,
  selectOrderHasFunctionalDepreciation,
} from '../../../redux/features/selectors/selectOrderDepreciation.ts';
import { selectAivReconciliationData, selectArvReconciliationData, selectReconciliationData } from '../../../redux/features/selectors/selectReconcilationData.ts';
import { selectReconciliation } from '../../../redux/features/selectors/selectReconciliation.ts';
import {
  selectSubjectArvValue,
  selectSubjectValue,
} from '../../../redux/features/selectors/selectSubjectValue.ts';
import { selectTotalBasementCost } from '../../../redux/features/selectors/selectTotalBasementCost.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import {
  CompPropertyDataState,
  RootState,
  SubjectPropertyDataState,
} from '../../../redux/types.ts';
import { getMapboxStaticURL } from '../../../utils/getMapboxStaticUrl.ts';
import { useAlerts } from '../../referenceView/AlertsSheet/hooks/useAlerts.tsx';
import { useTkv2SiteValueChartsParams } from '../../referenceView/utils/useTkv2SiteValueChartsParams.ts';
import {
  CompDataForMap,
  Coordinate,
  ReconciliationPageData,
  SalesComparisonGridColumnData,
  Tkv2PDF,
  costApproachTableData,
} from '../types.ts';
import { convertSalesComparisonDataToPDFGridColumn } from './convertSalesComparisonDataToPDFGridColumn.ts';
import { getSubjectPropertyImageUrl } from './shared.tsx';

export const useTkv2PDF = (): Tkv2PDF => {
  const order = useAppSelector(selectOrder);
  const compSequence = useAppSelector(selectCompSequence);
  const hasArvOption = useAppSelector(selectIsArvOrder);
  const propertyData = useAppSelector(selectPropertyDataEntities);
  const compTypes = useAppSelector(selectCompTypes) as Record<string, CompType | undefined>;
  const adjustments = useAppSelector(selectCompAdjustments);
  const adjustmentSettings = useAppSelector(selectAdjustmentSettings);
  const manualAdjustmentsEntities = useAppSelector(selectManualAdjustmentsEntities);
  const commentaryV2 = useAppSelector(selectCommentaryV2);
  const adjustmentsTableData = useAppSelector(selectAllAdjustmentsTableData);
  const arvAdjustmentsTableData = useAppSelector(selectAllArvAdjustmentsTableData);
  const appraiserName = useAppSelector(selectFullNameWithDefault);
  const priorSalesState = useAppSelector(selectPriorSales);
  const dateOfSaleChartParams = useAppSelector(selectMarketAdjustmentsCommentaryData)?.chart;
  const reconciliation = useAppSelector(selectReconciliation);
  const effectiveYear = useAppSelector(selectEffectiveYear);
  const context = useToolkitContext();
  const aivReconciliationSummarizedValues = useAppSelector(selectAivReconciliationData);
  const arvReconciliationSummarizedValues = useAppSelector(selectArvReconciliationData);
  const { fetcherOptions } = context;

  const hasFunctionalDepreciation = useAppSelector(selectOrderHasFunctionalDepreciation);
  const hasExternalDepreciation = useAppSelector(selectOrderHasExternalDepreciation);

  const siteValueChartParams = useTkv2SiteValueChartsParams();
  const alerts = useAlerts();
  const marketAggregateData = useAppSelector(selectCurrentMarketAggregateData);

  const subject: SubjectData | undefined = useMemo(
    () => (propertyData?.subject as SubjectPropertyDataState)?.subjectData,
    [propertyData],
  );

  const {
    address: subjectAddressRaw,
    zoning,
    zoningCompliance,
    parcelNumber,
    hoa,
    hoaFeeAmount,
    propertyInterest,
    propertyInterestOther,
    hoaFeeFrequency,
    legalDescription,
    coordinates,
  } = subject?.editableData?.propertyIdentification as PropertyIdentification;

  const propertyInterestValue =
    propertyInterest === 'Other' ? propertyInterestOther : propertyInterest;
  const appraisalType = useMemo(
    () =>
      ((order.appraisalType as string) ?? 'Appraisal')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase()),
    [order.appraisalType],
  );

  const subjectAddress = useMemo(
    () => orUndefined(addressToString)(subjectAddressRaw),
    [subjectAddressRaw],
  );

  const effectiveDate = useMemo(
    () =>
      (order.effectiveDate
        ? AloftDate.schema.parse(order.effectiveDate)
        : AloftDate.today()
      ).format('MM/dd/yyyy'),
    [order.effectiveDate],
  );
  const inspectionDate = useMemo(
    () =>
      order.inspectionDate
        ? AloftDate.schema.parse(order.inspectionDate).format('MM/dd/yyyy')
        : undefined,
    [order.inspectionDate],
  );

  const { data } = useRecommendedProperties({
    orderID: order.id,
  });

  const entityMediaURLs = useMemo(() => {
    const entries: Record<string, RESOMedia[]> = {};
    ['subject', ...compSequence].forEach((id) => {
      const propertyDataForEntity = propertyData?.[id];
      if (id === 'subject') {
        const { subjectData } = propertyDataForEntity as SubjectPropertyDataState;
        if (subjectData?.readonlyData?.backupListingData?.listing?.Media) {
          entries[id] = subjectData.readonlyData.backupListingData.listing.Media.filter(
            (mediaItem) => !!mediaItem.blobID,
          );
        }
      } else {
        const { compData } = propertyDataForEntity as CompPropertyDataState;
        if (compData?.readonlyData?.backupListingData?.listing?.Media) {
          entries[id] = compData.readonlyData.backupListingData.listing.Media.filter(
            (mediaItem) => !!mediaItem.blobID,
          );
        }
      }
    });
    return entries;
  }, [propertyData, compSequence]);

  const [entityListingPhotos, setEntityListingPhotos] = useState<{ [key: string]: Blob[] }>({});

  useEffect(() => {
    const fetchPhotos: () => Promise<{ [key: string]: Blob[] }> = async () => {
      const results = await Promise.all(
        Object.keys(entityMediaURLs).map(async (id) => {
          const media = entityMediaURLs[id];
          const photos = await Promise.all(
            (media ?? [])
              .filter((item) => !!item.blobID)
              .map(async (item) => {
                try {
                  const blobResult = await fetchGetBlob({
                    ...fetcherOptions,
                    pathParams: { blobID: item.blobID! },
                  });
                  if (blobResult instanceof Blob) {
                    return blobResult;
                  }
                } catch {
                  return undefined;
                }
                return undefined;
              }),
          );

          return [id, photos.filter((photo): photo is Blob => !!photo)];
        }),
      );

      return Object.fromEntries(results) as { [key: string]: Blob[] };
    };

    fetchPhotos()
      .then((photosByEntityId) => {
        setEntityListingPhotos(photosByEntityId);
      })
      .catch(() => {
        setEntityListingPhotos({});
      });
  }, [entityMediaURLs]);

  const salesComparablePhotosData = useMemo(
    () =>
      [...compSequence]
        .filter((id) => entityListingPhotos[id] && entityListingPhotos[id].length > 0)
        .filter((id) => !!propertyData[id])
        .map((id) => {
          const compData = propertyData[id] as CompPropertyDataState;
          const salePrice = compData.compData?.editableData.transaction?.price
            ? AloftPrice.schema.parse(compData.compData?.editableData.transaction?.price)
            : undefined;
          const listPrice = compData.compData?.editableData.transaction?.originalListPrice
            ? AloftPrice.schema.parse(
              compData.compData?.editableData.transaction?.originalListPrice,
            )
            : undefined;
          return {
            id,
            address: addressToString(
              compData.compData?.editableData?.propertyIdentification?.address,
            ),
            compType: compData.compData.compType,
            price:
              compData.compData.editableData.transaction.status === 'SettledSale'
                ? salePrice
                : listPrice,
            photos: entityListingPhotos[id]!,
            transactionText:
              compData.compData?.editableData.transaction.status === 'SettledSale'
                ? 'Sale Price'
                : 'List Price',
          };
        }),
    [compSequence, propertyData, entityListingPhotos],
  );

  const mlsPhotoUrl = useMemo(() => getSubjectPropertyImageUrl(subject), [subject]);
  const streetViewStaticUrl = useStreetViewStaticUrl(
    subjectAddress,
    {
      lat: coordinates?.latitude,
      lng: coordinates?.longitude,
    },
    !mlsPhotoUrl,
  );
  const satelliteViewStaticUrl = useMemo(() => {
    const lat = coordinates?.latitude;
    const lng = coordinates?.longitude;
    if (lat && lng) {
      return getMapboxStaticURL(lat, lng, '400x400', 'satellite');
    }
    return undefined;
  }, [coordinates]);
  const propertyImage = useMemo(
    () => [entityListingPhotos?.subject?.[0], mlsPhotoUrl, streetViewStaticUrl].filter(Boolean)[0],
    [entityListingPhotos, mlsPhotoUrl, streetViewStaticUrl],
  );

  const shortPropertyType = useMemo(
    () => String(formatShortenedPropertyType(order.propertyType)),
    [order.propertyType],
  );

  const hoaFees = useMemo(
    () =>
      hoaFeeAmount && hoaFeeFrequency
        ? `${AloftPrice.schema.parse(hoaFeeAmount).toString(true)} (${hoaFeeFrequency})`
        : undefined,
    [hoaFeeAmount, hoaFeeFrequency],
  );

  const [subjectSalesComparisonData, setSubjectSalesComparisonData] =
    useState<SalesComparisonGridColumnData | null>(null);

  const [subjectArvSalesComparisonData, setSubjectArvSalesComparisonData] =
    useState<SalesComparisonGridColumnData | null>(null);

  useEffect(() => {
    if (subject) {
      convertSalesComparisonDataToPDFGridColumn(
        'subject',
        compSequence,
        subject,
        undefined,
        undefined,
        entityListingPhotos?.subject,
        effectiveYear ? parseInt(effectiveYear, 10) : undefined,
      )
        .then(setSubjectSalesComparisonData)
        .catch(console.error);

      if (hasArvOption) {
        convertSalesComparisonDataToPDFGridColumn(
          'subjectArv',
          compSequence,
          subject,
          undefined,
          undefined,
          entityListingPhotos?.subject,
          effectiveYear ? parseInt(effectiveYear, 10) : undefined,
        )
          .then(setSubjectArvSalesComparisonData)
          .catch(console.error);
      }
    }
  }, [subject, entityListingPhotos]);

  const selectSubjectData = (
    selector: (state: RootState, key: string) => AloftPrice | AloftArea | undefined | null,
  ): AloftPrice | AloftArea | undefined | null =>
    useAppSelector((state: RootState) => selector(state, 'subject'));

  const selectArvSubjectData = (
    selector: (state: RootState, key: string) => AloftPrice | AloftArea | undefined | null,
  ): AloftPrice | AloftArea | undefined | null =>
    useAppSelector((state: RootState) => selector(state, 'subjectArv'));

  const reconciliationPageData: ReconciliationPageData = {
    ...commentaryV2?.reconciliation,
    aiv: { ...aivReconciliationSummarizedValues },
  };

  const costApproachExportData = useAppSelector(selectAllCostApproachExportData);
  const arvCostApproachExportData = useAppSelector(selectAllCostApproachExportData);
  const subjectOpinionOfSiteValue = useAppSelector(selectOpinionOfSiteValuePrice);

  const costApproachData: costApproachTableData = {
    subject: {
      opinionOfSiteValue: subjectOpinionOfSiteValue,
      dwelling: {
        area: selectSubjectData(selectImprovementsGla) as AloftArea | undefined,
        cost: selectSubjectData(selectGlaAndBathCostNew) as AloftPrice | undefined,
      },
      basement: {
        area: selectSubjectData(selectImprovementsBasement) as AloftArea | undefined,
        cost: selectSubjectData(selectTotalBasementCost) as AloftPrice | undefined,
      },
      additionalStructures: selectSubjectData(selectAdditionalStructureCostNew) as
        | AloftPrice
        | undefined
        | null,
      garageCarport: {
        area: selectSubjectData(selectImprovementsGarageCarport) as AloftArea | undefined,
        cost: selectSubjectData(selectGarageCarportCost) as AloftPrice | undefined,
      },
      totalEstimateCostNew: selectSubjectData(selectCostNew) as AloftPrice | undefined,
      depreciation: {
        physical: selectSubjectData(selectPhysicalDepreciation) as AloftPrice | undefined,
        functional: selectSubjectData(selectFunctionalDepreciation) as AloftPrice | undefined,
        external: selectSubjectData(selectExternalDepreciation) as AloftPrice | undefined,
        total: selectSubjectData(selectDepreciation) as AloftPrice | undefined,
      },
      depreciatedCostOfImprovement: selectSubjectData(selectDepreciatedCostOfImprovements) as
        | AloftPrice
        | undefined,
      indicatedValueByCostApproach: selectSubjectData(selectSubjectValue) as AloftPrice | undefined,
      effectiveDate: costApproachExportData.effectiveDateYear,
      qualityRating: costApproachExportData.quality,
      remainingEconomicLife: costApproachExportData.remainingEconomicLife,
    },
  };

  if (hasArvOption) {
    costApproachData.subjectArv = {
      opinionOfSiteValue: subjectOpinionOfSiteValue,
      dwelling: {
        area: selectArvSubjectData(selectImprovementsGla) as AloftArea | undefined,
        cost: selectArvSubjectData(selectGlaAndBathCostNew) as AloftPrice | undefined,
      },
      basement: {
        area: selectArvSubjectData(selectImprovementsBasement) as AloftArea | undefined,
        cost: selectArvSubjectData(selectTotalBasementCost) as AloftPrice | undefined,
      },
      additionalStructures: selectArvSubjectData(selectAdditionalStructureCostNew) as
        | AloftPrice
        | undefined
        | null,
      garageCarport: {
        area: selectArvSubjectData(selectImprovementsGarageCarport) as AloftArea | undefined,
        cost: selectArvSubjectData(selectGarageCarportCost) as AloftPrice | undefined,
      },
      totalEstimateCostNew: selectArvSubjectData(selectCostNew) as AloftPrice | undefined,
      depreciation: {
        physical: selectArvSubjectData(selectPhysicalDepreciation) as AloftPrice | undefined,
        functional: selectArvSubjectData(selectFunctionalDepreciation) as AloftPrice | undefined,
        external: selectArvSubjectData(selectExternalDepreciation) as AloftPrice | undefined,
        total: selectArvSubjectData(selectDepreciation) as AloftPrice | undefined,
      },
      depreciatedCostOfImprovement: selectArvSubjectData(selectDepreciatedCostOfImprovements) as
        | AloftPrice
        | undefined,
      indicatedValueByCostApproach: selectArvSubjectData(selectSubjectArvValue) as
        | AloftPrice
        | undefined,
      effectiveDate: arvCostApproachExportData.effectiveDateYear,
      qualityRating: arvCostApproachExportData.quality,
      remainingEconomicLife: arvCostApproachExportData.remainingEconomicLife,
    };

    reconciliationPageData.arv = {
      ...arvReconciliationSummarizedValues,
    };
  }

  const [comparablesSalesComparisonData, setComparablesSalesComparisonData] = useState<
    Record<string, SalesComparisonGridColumnData>
  >({});

  useEffect(() => {
    const loadComparablesData = async () => {
      const compPromises = compSequence
        .filter((compId) => propertyData[compId] && adjustments[compId])
        .map(async (compId) => {
          const comp = await convertSalesComparisonDataToPDFGridColumn(
            compId,
            compSequence,
            (propertyData[compId] as CompPropertyDataState).compData,
            adjustments[compId],
            coordinates,
            entityListingPhotos?.[compId],
            effectiveYear ? parseInt(effectiveYear, 10) : undefined,
          );
          return comp !== null ? ([compId, comp] as const) : null;
        });

      const results = await Promise.all(compPromises);
      const newData: Record<string, SalesComparisonGridColumnData> = Object.fromEntries(
        results.filter(
          (result): result is [string, SalesComparisonGridColumnData] => result !== null,
        ),
      );

      setComparablesSalesComparisonData(newData);
    };

    if (compSequence.length > 0) loadComparablesData().then(noop).catch(console.error);
  }, [compSequence, propertyData, adjustments, coordinates, entityListingPhotos]);
  const compsForMaps: CompDataForMap[] = compSequence.reduce((acc, compId) => {
    if (propertyData[compId]) {
      const comp = propertyData[compId] as CompPropertyDataState;
      acc.push({
        id: comp.id,
        address: comp.compData?.editableData?.propertyIdentification?.address.AddressLineText,
        latitude: comp?.compData.editableData.propertyIdentification.coordinates?.latitude ?? 0,
        longitude: comp?.compData.editableData.propertyIdentification.coordinates?.longitude ?? 0,
        mlsStatus: comp?.compData?.editableData?.transaction.status,
        saleDate: comp?.compData?.editableData?.transaction?.saleDate,
      });
    }
    return acc;
  }, [] as CompDataForMap[]);

  const subjectCoordinates = {
    latitude: subject.editableData?.propertyIdentification?.coordinates?.latitude ?? 0,
    longitude: subject.editableData?.propertyIdentification?.coordinates?.longitude ?? 0,
  };

  const signedAt = order.signedAt
    ? AloftTime.schema.parse(order.signedAt).toAloftDate().format('MM/dd/yyyy')
    : undefined;
  type SearchCriteria = {
    coordinates?: { latitude: number; longitude: number }[];
    radius?: number;
  };
  const searchCriteria: SearchCriteria =
    data?.body?.output && typeof data.body.output === 'object' && !('error' in data.body.output)
      ? (data.body.output as { searchCriteria: SearchCriteria }).searchCriteria
      : {};

  let mapBoxCoordinates: Coordinate[] | undefined;

  if (searchCriteria) {
    if (Array.isArray(searchCriteria.coordinates) && searchCriteria.coordinates.length > 0) {
      mapBoxCoordinates = searchCriteria.coordinates;
    } else if (searchCriteria.radius) {
      mapBoxCoordinates = createCircleCoordinates(
        subjectCoordinates,
        searchCriteria.radius * 1000,
        64,
      )
        .map(([lon, lat]) => ({ longitude: lon, latitude: lat }))
        .filter(
          (coord): coord is Coordinate =>
            coord.latitude !== undefined && coord.longitude !== undefined,
        );
    }
  }
  let statistics: RecommendedCompsStatistics | undefined;

  if (
    (data?.source === 'constellation1' || data?.source === 'abode') &&
    data?.body?.status === 'found'
  ) {
    statistics = data.body?.output?.statistics;
  }

  const priorSales = Object.fromEntries(
    [
      priorSalesState.subject && ['subject', priorSalesState.subject],
      ...compSequence
        .filter((id) => priorSalesState[id])
        .map((id) => priorSalesState[id] && [id, priorSalesState[id]]),
    ].filter(Boolean) as [string, PriorSales][],
  );

  const {
    includeTotalCostToCure,
    opinionOfMarketValue,
    totalCostToCure,
    totalRenovationBudget,
    opinionOfAfterRepairValue,
    includeOpinionOfAfterRepairValue,
    includeTotalRenovationBudget,
  } = reconciliation;

  const { revisionData } = order;
  let revisionType;

  if (revisionData) {
    revisionType = formatLongRevisionType(revisionData.revisionType);
  }

  const backupListing = subject?.readonlyData.backupListingData?.listing;

  const { CountyOrParish, CountyOrParishFIPS } = backupListing || {};

  const estimatedROI =
    hasArvOption && opinionOfAfterRepairValue && opinionOfMarketValue
      ? (() => {
        const arv = AloftPrice.schema.parse(opinionOfAfterRepairValue);
        const aiv = AloftPrice.schema.parse(opinionOfMarketValue);
        const budget = totalRenovationBudget
          ? AloftPrice.schema.parse(totalRenovationBudget)
          : AloftPrice.zero;
        return arv.sub(aiv).sub(budget);
      })()
      : undefined;

  const hasNegativeROI = estimatedROI?.le(AloftPrice.zero);

  const estimatedROIString = estimatedROI ? estimatedROI.toString(true) : undefined;

  return {
    subjectAddress: subjectAddress ?? '',
    opinionOfMarketValue: opinionOfMarketValue
      ? AloftPrice.schema.parse(opinionOfMarketValue).toString(true)
      : '-',
    totalCostToCure: totalCostToCure
      ? AloftPrice.schema.parse(totalCostToCure).toString(true)
      : '-',
    totalRenovationBudget: totalRenovationBudget
      ? AloftPrice.schema.parse(totalRenovationBudget).toString(true)
      : undefined,
    opinionOfAfterRepairValue: opinionOfAfterRepairValue
      ? AloftPrice.schema.parse(opinionOfAfterRepairValue).toString(true)
      : undefined,
    estimatedROI: estimatedROIString,
    hasNegativeROI,
    includeTotalCostToCure,
    includeTotalRenovationBudget,
    includeOpinionOfAfterRepairValue,
    clientName: order.appraisifyName ?? APPRAISIFY_NAME,
    clientAddress: order.appraisifyAddress ?? APPRAISIFY_ADDRESS,
    appraiserFee: order.appraiserFee ?? AloftPrice.zero.toJSON(),
    amcFee: order.appraisifyFee ?? AloftPrice.zero.toJSON(),
    borrowerName: order.borrowerName ?? '',
    mapBoxCoordinates,
    mapStatistics: statistics,
    intendedUsers: order.intendedUsers,
    intendedUse: order.intendedUse,
    propertyType: order.propertyType ?? 'singleFamilyResidence',
    shortPropertyType,
    inspectorType: order.inspectorType ?? '',
    appraisalType,
    propertyImage,
    effectiveDate,
    inspectionDate,
    zoning,
    zoningCompliance,
    parcelNumber,
    propertyInterest: propertyInterestValue,
    hoa,
    hoaFees,
    legalDescription,
    propertyMapImage: satelliteViewStaticUrl,
    subjectSalesComparisonData,
    subjectArvSalesComparisonData,
    comparablesSalesComparisonData,
    compsForMaps,
    subjectCoordinates,
    adjustmentSettings,
    manualAdjustmentsEntities,
    commentaryV2,
    adjustmentsTableData,
    arvAdjustmentsTableData,
    hasFunctionalDepreciation,
    hasExternalDepreciation,
    chartParams: {
      dateOfSale: dateOfSaleChartParams,
      site: siteValueChartParams,
    },
    licenseState: order.licenseState,
    licenseNumber: order.licenseNumber,
    licenseExpiration: order.licenseExpiration
      ? AloftDate.schema.parse(order.licenseExpiration).format('MM/dd/yyyy')
      : undefined,
    licenseType: order.licenseType,
    signedAt,
    appraiserName: appraiserName ?? '-',
    inspectionData: order.inspectionData,
    costApproachData,
    compSequence,
    priorSales,
    reconciliationPageData,
    isRevision: order.isRevision || false,
    revisionType,
    alerts: Object.values(alerts).flat(),
    marketAggregateData,
    countyName: CountyOrParish,
    countyFIPS: CountyOrParishFIPS,
    revisionData: order.revisionData,
    salesComparablePhotosData,
    hasArvOption,
    compTypes,
  };
};
